import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  ClockIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { TIMEZONES, TIMEZONE_GROUPS } from '../../constants/timezones';

export default function CompanySettings() {
  const { getCurrentCompany } = useAuthStore();
  const currentCompany = getCurrentCompany();
  const companyId = currentCompany?._id || currentCompany;
  const queryClient = useQueryClient();
  
  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => apiHelpers.getCompany(companyId),
    enabled: !!companyId,
    retry: 1
  });
  
  // Fetch available currencies
  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => apiHelpers.getCurrencies(),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
  
  const currencies = currenciesData?.data?.currencies || [];
  const defaultCurrency = currencies.find(c => c.isDefault) || currencies[0];
  
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    settings: {
      defaultTimezone: 'UTC',
      dateFormat: 'YYYY-MM-DD',
      allowExternalSharing: false,
      preferredCurrency: ''
    }
  });
  
  useEffect(() => {
    if (data?.data?.company) {
      const c = data.data.company;
      setFormData({
        name: c.name || '',
        industry: c.industry || '',
        website: c.website || '',
        settings: {
          defaultTimezone: c.settings?.defaultTimezone || 'UTC',
          dateFormat: c.settings?.dateFormat || 'YYYY-MM-DD',
          allowExternalSharing: c.settings?.allowExternalSharing || false,
          preferredCurrency: c.settings?.preferredCurrency || ''
        }
      });
    }
  }, [data]);
  
  const updateMutation = useMutation({
    mutationFn: (updates) => apiHelpers.updateCompany(companyId, updates),
    onSuccess: () => {
      toast.success('Settings saved');
      queryClient.invalidateQueries(['company', companyId]);
    },
    onError: (error) => {
      console.error('Update company error:', error);
      toast.error(error.response?.data?.error || 'Failed to save settings');
    }
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };
  
  // Show message if no company selected (for Super Admins and Managing Admins)
  if (!companyId) {
    return (
      <div className="max-w-3xl animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Company Settings
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Manage your company profile and preferences
          </p>
        </div>
        <div className="card p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <BuildingOfficeIcon className="w-12 h-12 text-surface-400 mb-4" />
            <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              No Company Selected
            </h3>
            <p className="text-surface-500 dark:text-surface-400 max-w-md">
              Please select a company from the sidebar to manage its settings.
            </p>
            <a 
              href="/#/admin/companies" 
              className="btn-primary mt-4"
            >
              Select a Company
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-8" />
        <div className="card p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton h-12" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="max-w-3xl animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Company Settings
          </h1>
        </div>
        <div className="card p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <BuildingOfficeIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              Failed to Load Company
            </h3>
            <p className="text-surface-500 dark:text-surface-400 max-w-md">
              {error?.response?.data?.error || error?.message || 'Unable to load company settings. Please try again.'}
            </p>
            <button 
              onClick={() => queryClient.invalidateQueries(['company', companyId])}
              className="btn-primary mt-4"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const companyData = data?.data?.company;
  
  return (
    <div className="max-w-3xl animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
          Company Settings
        </h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Manage your company profile and preferences
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Profile */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center gap-3">
            <BuildingOfficeIcon className="w-5 h-5 text-surface-500" />
            <h2 className="font-medium text-surface-900 dark:text-white">Company Profile</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="label">Company Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
              />
            </div>
            
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="e.g., Technology, Healthcare, Finance"
                className="input"
              />
            </div>
            
            <div>
              <label className="label">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://example.com"
                className="input"
              />
            </div>
          </div>
        </div>
        
        {/* Preferences */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center gap-3">
            <Cog6ToothIcon className="w-5 h-5 text-surface-500" />
            <h2 className="font-medium text-surface-900 dark:text-white">Preferences</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="label">Default Timezone</label>
              <select
                value={formData.settings.defaultTimezone}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, defaultTimezone: e.target.value }
                })}
                className="input"
              >
                {TIMEZONE_GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {TIMEZONES.filter(tz => tz.group === group).map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-surface-500 mt-1">
                This timezone will be used for AI responses and date/time formatting
              </p>
            </div>
            
            <div>
              <label className="label">Date Format</label>
              <select
                value={formData.settings.dateFormat}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, dateFormat: e.target.value }
                })}
                className="input"
              >
                <option value="YYYY-MM-DD">2024-01-15</option>
                <option value="MM/DD/YYYY">01/15/2024</option>
                <option value="DD/MM/YYYY">15/01/2024</option>
                <option value="MMM DD, YYYY">Jan 15, 2024</option>
              </select>
            </div>
            
            {/* Preferred Payment Currency */}
            {currencies.length > 0 && (
              <div>
                <label className="label flex items-center gap-2">
                  <CurrencyDollarIcon className="w-4 h-4" />
                  Preferred Payment Currency
                </label>
                <select
                  value={formData.settings.preferredCurrency || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, preferredCurrency: e.target.value }
                  })}
                  className="input"
                >
                  <option value="">Use system default ({defaultCurrency?.code || 'USD'})</option>
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name} ({currency.symbol})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-surface-500 mt-1">
                  This currency will be used as the default for all billing transactions
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-surface-900 dark:text-white">
                  Allow External Sharing
                </label>
                <p className="text-sm text-surface-500">
                  Enable public dashboard links
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  settings: { ...formData.settings, allowExternalSharing: !formData.settings.allowExternalSharing }
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.settings.allowExternalSharing ? 'bg-primary-600' : 'bg-surface-200 dark:bg-surface-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.settings.allowExternalSharing ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        
        {/* Plan Info */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-surface-900 dark:text-white">Current Plan</h3>
              <p className="text-sm text-surface-500">{companyData?.billingPlan?.toUpperCase() || 'FREE'}</p>
            </div>
            <span className="badge-primary capitalize">
              {companyData?.billingPlan || 'free'}
            </span>
          </div>
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            {updateMutation.isPending && (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            )}
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}

